import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ignoreDomainSwitchHttpStatus, L1L3AuthService, SwitchDomainService } from 'ngx-auth-util-lib';
import {
  DomainType,
  NgxHekaDesignLibModule,
  SearchInputComponent,
  TableLayoutComponent,
  UserDomainSummaryDTO
} from 'ngx-heka-design-lib';
import { ERROR_CODES } from 'src/app/models/codes';
import { leftDomainNameQueryParam } from 'src/app/models/multi-domain';
import { DomainHandlerService } from 'src/app/services/domain-handler-service';
import { NotificationService } from 'src/app/services/error-handling/notification.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { MultiDomainMgmtService } from 'src/app/services/multi-domain-mgmt.service';
import { MultiDomainLastAdminErrorDialogComponent } from '../multi-domain-last-admin-error-dialog/multi-domain-last-admin-error-dialog.component';
import {
  MultiDomainLeaveDialogComponent,
  MultiDomainLeaveResult
} from '../multi-domain-leave-dialog/multi-domain-leave-dialog.component';
import {environment} from "../../../environments/local/environment";

@Component({
  selector: 'app-multi-domain-list',
  imports: [
    CommonModule,
    TranslateModule,
    MatIcon,
    MatButtonModule,
    MatTableModule,
    MatMenuModule,
    MatDialogModule,
    NgxHekaDesignLibModule,
    TableLayoutComponent,
    SearchInputComponent
  ],
  templateUrl: './multi-domain-list.component.html',
  standalone: true,
  styleUrl: './multi-domain-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiDomainListComponent {
  readonly domains = input<UserDomainSummaryDTO[]>([]);
  readonly onCreateDomain = output<void>();

  domainTypes = DomainType;
  displayedColumns= signal<string[]>(['companyName', 'domainState', 'actions']);
  readonly searchText = signal('');
  readonly hasSanctionedDomain = computed(() =>
    !!this.domains()?.find(d => d.domainType == DomainType.BLACKLISTED)
  );
  readonly filteredDomains = computed(() => {
    const searchText = this.searchText();
    if (searchText) {
      return this.domains().filter(
        d => d.companyName.toLowerCase().indexOf(searchText.toLowerCase()) > -1
      );
    }
    return [...this.domains()];
  });

  constructor(
    private dialog: MatDialog,
    public loadingService: LoadingService,
    private switchDomainService: SwitchDomainService,
    private router: Router,
    private authService: L1L3AuthService,
    private domainHandlerService: DomainHandlerService,
    private multiDomainMgmtService: MultiDomainMgmtService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private location: Location
  ) {}

  searchTextChanged(searchText: string) {
    this.searchText.set(searchText);
  }

  onLeaveDomain(dto: UserDomainSummaryDTO) {
    let availableDomains: UserDomainSummaryDTO[] = [];

    if (dto.active) {
      availableDomains = this.domains().filter(d => d.domainId != dto.domainId && d.domainType == DomainType.JOINED);
    }
    const dialogRef = this.dialog.open(MultiDomainLeaveDialogComponent, {
      autoFocus: false,
      ...environment.dialogs.small,
      data: { domainToLeave: dto, availableDomains }
    });
    dialogRef.afterClosed().subscribe({
      next: (result?: MultiDomainLeaveResult) => {
        if (result) {
          this.leaveDomain(dto, result.selectedDomain?.domainId);
        }
      }
    });
  }

  private leaveDomain(leaveDomain: UserDomainSummaryDTO, electedDomainId?: string) {
    this.loadingService.setLoading(true);
    this.multiDomainMgmtService.leaveDomain(leaveDomain.domainId, electedDomainId).subscribe({
      next: () => {
        if (leaveDomain.active) {
          const targetPath = `${this.location.path()}?${leftDomainNameQueryParam}=${encodeURIComponent(
            leaveDomain.companyName
          )}`;

          if (electedDomainId) {
            this.switchDomainService.switchDomain(electedDomainId).subscribe({
              error: err => {
                // here the refresh-logic would not be triggered, so instead reloading the application.
                this.domainHandlerService.reloadApplication(targetPath);
              },
              complete: () => this.domainHandlerService.reloadApplication(targetPath)
            });
          } else {
            this.authService.clearL3Token();
            this.domainHandlerService.reloadApplication(targetPath);
          }
        } else {
          this.loadingService.setLoading(false);
          this.multiDomainMgmtService.updateMultiDomainProfile();
          this.notificationService.showSuccess(
            this.translateService.instant('MULTI_DOMAIN.LEAVE.NOTIFICATION', { companyName: leaveDomain.companyName })
          );
        }
      },
      error: err => {
        this.loadingService.setLoading(false);

        if (err?.status == 400) {
          const errorCode: number = err.error?.code;

          if (errorCode == ERROR_CODES.USERS_NO_PROTECTED_APP_ACCESS) {
            return this.showLastAdminError(leaveDomain);
          }
        }
        throw err;
      }
    });
  }

  private showLastAdminError(dto: UserDomainSummaryDTO) {
    this.dialog.open(MultiDomainLastAdminErrorDialogComponent, {
      ...environment.dialogs.small,
      data: { companyName: dto.companyName }
    });
  }

  onEditDomain(dto: UserDomainSummaryDTO) {
    const domainEditRoute = 'pages/account-settings/company-data;edit=true';

    if (dto.active) {
      this.router.navigateByUrl(domainEditRoute);
    } else {
      this.switchDomainWithRoute(dto.domainId, domainEditRoute);
    }
  }

  onDeleteDomain(dto: UserDomainSummaryDTO) {
    const domainSettingsRoute = 'pages/account-settings/extended';

    if (dto.active) {
      this.router.navigateByUrl(domainSettingsRoute);
    } else {
      this.switchDomainWithRoute(dto.domainId, domainSettingsRoute);
    }
  }

  private switchDomainWithRoute(domainId: string, route: string) {
    this.loadingService.setLoading(true);
    this.switchDomainService.switchDomain(domainId).subscribe({
      error: err => {
        this.loadingService.setLoading(false);
        if (err?.status != ignoreDomainSwitchHttpStatus) {
          throw err;
        }
        this.router.navigateByUrl(route);
      },
      complete: () => {
        this.loadingService.setLoading(false);
        this.router.navigateByUrl(route);
      }
    });
  }
}
